/**
 * PitchRehearsalPlayer — CON-1610. Public playback of a recorded rehearsal.
 *
 * Patrick 2026-08-12: "so I can send it to someone to play it back and also for
 * me to have different versions … and send samples to people to judge."
 *
 * 🔴 RENDERS ABOVE THE LOGIN GATE (App.jsx), like LivePitchView. The share token
 * is the credential. It exposes ONE recording's audio + the published slides, and
 * never the speaker notes — a judge should hear the pitch, not read the script.
 *
 * 🔴 THE AUDIO ELEMENT IS THE CLOCK. Slide position is derived from
 * audio.currentTime against the timeline on every timeupdate — never from a
 * setInterval running alongside. That means scrubbing, pausing, and seeking are
 * correct for free, because there is only one source of time.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SlideCanvas from './SlideCanvas';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://core.honestlee.app').replace(/\/$/, '');

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function PitchRehearsalPlayer() {
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tMs, setTMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [showCounter, setShowCounter] = useState(true);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const audioRef = useRef(null);

  useEffect(() => {
    if (!token) { setErr('This link is missing its code.'); return undefined; }
    let alive = true;
    fetch(`${API}/api/pitch/rehearsal/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        if (j.success === false) { setErr('This recording is not available.'); return; }
        setData(j);
        setShowCounter(j.rehearsal?.show_counter !== false);
        setDur((j.rehearsal?.duration_ms || 0) / 1000);
      })
      .catch(() => alive && setErr('Could not reach the server.'));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    const r = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', r);
    window.addEventListener('orientationchange', r);
    return () => { window.removeEventListener('resize', r); window.removeEventListener('orientationchange', r); };
  }, []);

  const timeline = data?.rehearsal?.timeline || [];
  const slides = data?.slides || [];

  // Which slide is showing at tMs — the last mark at or before now.
  // Falls back to position when a slide has since been deleted, so an old
  // recording still plays end-to-end instead of blanking.
  /* CON-1640 — the mark now carries the SUB-SLIDE step too, so a recording of a
     slide with four screenshots plays them back in time with the audio instead
     of holding the first picture for the whole passage.
     Older recordings have no `variant` on their marks; those read as 0, which is
     the base image — exactly how they played when they were made. */
  const cur = useMemo(() => {
    let c = timeline[0];
    for (let i = 0; i < timeline.length; i += 1) {
      if (timeline[i].t <= tMs) c = timeline[i]; else break;
    }
    return c;
  }, [timeline, tMs]);

  const slide = useMemo(() => {
    if (!slides.length) return null;
    if (!cur) return slides[0];
    return slides.find((s) => Number(s.id) === Number(cur.slide_id))
        || slides[Math.min(cur.idx || 0, slides.length - 1)]
        || slides[0];
  }, [cur, slides]);

  const variantIndex = Number(cur?.variant) || 0;

  const slideNo = useMemo(() => {
    if (!slide) return 0;
    const i = slides.findIndex((s) => Number(s.id) === Number(slide.id));
    return i < 0 ? 0 : i + 1;
  }, [slide, slides]);

  const theme = useMemo(() => {
    if (!data) return null;
    const key = slide?.theme_key || data.deck?.theme_key;
    return data.themes?.find((t) => t.key === key) || data.themes?.[0] || null;
  }, [data, slide]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
  }, []);

  const seek = useCallback((sec) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(dur || a.duration || 0, sec));
    setTMs(a.currentTime * 1000);
  }, [dur]);

  // Space to play/pause, arrows to jump between slide marks — the natural way to
  // review a run without hunting for the scrubber.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ') { e.preventDefault(); toggle(); }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const next = e.key === 'ArrowRight'
          ? timeline.find((m) => m.t > tMs + 50)
          : [...timeline].reverse().find((m) => m.t < tMs - 800);
        seek(((next ? next.t : (e.key === 'ArrowRight' ? (dur * 1000) : 0)) / 1000));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, timeline, tMs, seek, dur]);

  const box = { position: 'fixed', inset: 0, background: '#07090d', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#94a3b8',
                fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 };

  if (err) return <div style={box}><div><div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>{err}</div></div>;
  if (!data) return <div style={box}>Loading…</div>;

  const audioUrl = data.rehearsal?.audio_url;
  const BAR = 84;
  const availH = Math.max(120, size.h - BAR);
  const w = Math.min(size.w, (availH * 16) / 9);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        {slide ? <SlideCanvas slide={slide} theme={theme} deck={data.deck} width={w} variantIndex={variantIndex} /> : null}

        {/* The counter is drawn by the PLAYER, not burned into the recording, so
            it can be switched off for an audience that should just watch. */}
        {showCounter ? (
          <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                        background: '#0f172ad9', color: '#e2e8f0', padding: '4px 12px', borderRadius: 20,
                        fontSize: 12, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
                        border: '1px solid #1e293b', whiteSpace: 'nowrap' }}>
            {mmss(tMs / 1000)} / {mmss(dur)} · slide {slideNo}/{slides.length}
          </div>
        ) : null}
      </div>

      <div style={{ height: BAR, background: '#0b0f16', borderTop: '1px solid #1e293b',
                    display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px', boxSizing: 'border-box' }}>
        <button onClick={toggle}
          style={{ background: '#E46E34', color: '#fff', border: 0, borderRadius: 999, width: 46, height: 46,
                   fontSize: 19, cursor: 'pointer', flexShrink: 0 }}>
          {playing ? '❚❚' : '▶'}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4, whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {data.deck?.title}{data.rehearsal?.label ? ` · ${data.rehearsal.label}` : ''}
            {data.rehearsal?.recorded_at ? ` · ${new Date(data.rehearsal.recorded_at).toLocaleDateString()}` : ''}
          </div>
          <div style={{ position: 'relative' }}>
            <input type="range" min={0} max={Math.max(1, dur)} step={0.1} value={Math.min(tMs / 1000, dur || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#E46E34', cursor: 'pointer', display: 'block' }} />
            {/* Slide-change ticks: you can see the rhythm of the run at a glance,
                and how long you actually spent on each slide. */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {dur > 0 && timeline.map((m, i) => (
                <div key={i} style={{ position: 'absolute', left: `${Math.min(100, (m.t / 1000 / dur) * 100)}%`,
                                      top: -3, width: 2, height: 6, background: '#5eead4', opacity: 0.85 }} />
              ))}
            </div>
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {mmss(tMs / 1000)} / {mmss(dur)}
        </div>
        <button onClick={() => setShowCounter((v) => !v)} title="Show/hide the on-slide counter"
          style={{ background: showCounter ? '#134e4a' : '#1e293b', color: showCounter ? '#5eead4' : '#94a3b8',
                   border: '1px solid #334155', borderRadius: 8, padding: '7px 10px', fontSize: 11,
                   cursor: 'pointer', flexShrink: 0 }}>
          ⏱ counter
        </button>
      </div>

      {audioUrl ? (
        <audio ref={audioRef} src={audioUrl} preload="metadata"
          onTimeUpdate={(e) => setTMs(e.target.currentTime * 1000)}
          onLoadedMetadata={(e) => {
            // A webm from MediaRecorder often reports Infinity for duration, so the
            // recorded wall-clock value from the DB is the more reliable number.
            const d = e.target.duration;
            if (Number.isFinite(d) && d > 0) setDur(d);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          style={{ display: 'none' }} />
      ) : null}
    </div>
  );
}
